import { useRef, useState } from "react";
import { APP_STORE_URL, PLAY_STORE_URL } from "../seo/site";
import { CATEGORY_LABELS, PASS_RATIO, fill } from "./practiceContent";
import { track, trackOnce } from "../analytics/analytics";

/* ============================================================================
   The free sample quiz on the practice page.

   Questions come from the backend: GET {API}/public/sample-quiz?lang=sv
   (DriverBackend/routes/public_quiz.py). They are the max 10 questions an
   admin marked "Public" in the admin panel. Scoring happens here in the
   browser — no login, nothing is saved.

   The quiz moves through these steps ("phase"):

     intro ──Start──► loading ──► question ──(answer, Next)──► … ──► result
                         │                                           │
                         ├──► error  (network / too many requests)   │
                         └──► empty  (no public questions yet)       │
                                       ▲───────── Try again ─────────┘

   Questions are only fetched when the visitor presses Start, so search engine
   crawlers and people who just read the page don't call the API.

   Analytics (src/analytics/analytics.js): quiz_start when a round begins,
   quiz_complete when its last question is answered — once per round.
   ========================================================================== */

const API_URL = process.env.REACT_APP_API_URL || "/api";
const LETTERS = ["A", "B", "C", "D", "E", "F"];

async function fetchSampleQuestions(lang) {
  const res = await fetch(`${API_URL}/public/sample-quiz?lang=${lang}`);
  if (res.status === 429) throw new Error("too-many");
  if (!res.ok) throw new Error("failed");
  const data = await res.json();
  return Array.isArray(data.questions) ? data.questions : [];
}

export default function SampleQuiz({ lang, text }) {
  const [phase, setPhase] = useState("intro");
  const [errorKind, setErrorKind] = useState(null); // "too-many" | "failed"
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);            // current question
  const [picked, setPicked] = useState(null);       // chosen option, null = not answered yet
  const [score, setScore] = useState(0);
  const cardRef = useRef(null);
  const roundRef = useRef(0); // counts rounds (Start / Try again) for analytics

  const beginRound = (questionCount) => {
    roundRef.current += 1;
    track("quiz_start", { question_count: questionCount });
    setPhase("question");
  };

  // Keep the quiz card in view (on phones the next question would otherwise start below the fold).
  const focusCard = () => {
    requestAnimationFrame(() => {
      const card = cardRef.current;
      if (!card) return;
      if (card.getBoundingClientRect().top < 0) card.scrollIntoView({ behavior: "smooth", block: "start" });
      card.focus({ preventScroll: true });
    });
  };

  const start = async () => {
    setIndex(0);
    setPicked(null);
    setScore(0);

    // "Try again" after finishing reuses the questions we already have.
    if (questions.length > 0) {
      beginRound(questions.length);
      focusCard();
      return;
    }

    setPhase("loading");
    try {
      const loaded = await fetchSampleQuestions(lang);
      setQuestions(loaded);
      if (loaded.length > 0) beginRound(loaded.length);
      else setPhase("empty");
    } catch (err) {
      setErrorKind(err.message === "too-many" ? "too-many" : "failed");
      setPhase("error");
    }
    focusCard();
  };

  const answer = (optionIndex) => {
    if (picked !== null) return; // one answer per question
    setPicked(optionIndex);
    if (optionIndex === questions[index].correct_option) setScore((s) => s + 1);
  };

  const next = () => {
    if (index + 1 < questions.length) {
      setIndex(index + 1);
      setPicked(null);
    } else {
      setPhase("result");
      trackOnce(`quiz_complete:${roundRef.current}`, "quiz_complete", {
        score,
        question_count: questions.length,
        passed: score / questions.length >= PASS_RATIO,
      });
    }
    focusCard();
  };

  return (
    <section className="tk-quiz" aria-label={text.startTitle} ref={cardRef} tabIndex={-1}>
      {phase === "intro" && (
        <div className="tk-quiz-center">
          <h2 className="tk-quiz-title">{text.startTitle}</h2>
          <p className="tk-quiz-muted">{text.startText}</p>
          <button type="button" className="tk-btn tk-btn--primary" onClick={start}>{text.start}</button>
        </div>
      )}

      {phase === "loading" && (
        <p className="tk-quiz-center tk-quiz-muted" role="status">{text.loading}</p>
      )}

      {phase === "error" && (
        <div className="tk-quiz-center" role="alert">
          <p>{errorKind === "too-many" ? text.tooMany : text.error}</p>
          <button type="button" className="tk-btn tk-btn--primary" onClick={start}>{text.retry}</button>
        </div>
      )}

      {phase === "empty" && (
        <div className="tk-quiz-center" data-track-placement="quiz_unavailable">
          <p>{text.empty}</p>
          <StoreLinks text={text} />
        </div>
      )}

      {phase === "question" && (
        <Question
          key={index}
          lang={lang}
          text={text}
          question={questions[index]}
          number={index + 1}
          total={questions.length}
          picked={picked}
          onAnswer={answer}
          onNext={next}
          isLast={index + 1 === questions.length}
        />
      )}

      {phase === "result" && (
        <Result text={text} score={score} total={questions.length} onRestart={start} />
      )}
    </section>
  );
}

/* One question with its answer buttons, feedback and explanation. */
function Question({ lang, text, question, number, total, picked, onAnswer, onNext, isLast }) {
  const answered = picked !== null;
  const isCorrect = answered && picked === question.correct_option;
  const category = CATEGORY_LABELS[question.category]?.[lang];
  // Prefer the explanation written for the chosen option, else the general one.
  const explanation = (answered && question.option_explanations?.[picked]) || question.explanation;

  const optionClass = (i) => {
    if (!answered) return "tk-option";
    if (i === question.correct_option) return "tk-option is-correct";
    if (i === picked) return "tk-option is-wrong";
    return "tk-option is-dim";
  };

  return (
    <div>
      <div className="tk-quiz-meta">
        <span>{fill(text.questionOf, { n: number, total })}</span>
        {category && <span className="tk-quiz-chip">{category}</span>}
      </div>
      <div className="tk-progress" aria-hidden="true">
        <span style={{ width: `${((number - 1 + (answered ? 1 : 0)) / total) * 100}%` }} />
      </div>

      <h3 className="tk-quiz-question">{question.title}</h3>
      {question.image && (
        <img className="tk-quiz-image" src={`${API_URL}${question.image}`} alt="" width="600" height="600" />
      )}

      <div className="tk-options">
        {question.options.map((option, i) => (
          <button
            key={i}
            type="button"
            className={optionClass(i)}
            onClick={() => onAnswer(i)}
            disabled={answered}
            aria-pressed={picked === i}
          >
            <span className="tk-option-letter" aria-hidden="true">{LETTERS[i]}</span>
            <span>{option}</span>
          </button>
        ))}
      </div>

      {/* aria-live: screen readers announce the feedback as soon as it appears */}
      <div aria-live="polite">
        {answered && (
          <div className={isCorrect ? "tk-feedback is-correct" : "tk-feedback is-wrong"}>
            <p className="tk-feedback-title">
              {isCorrect ? text.correct : text.wrong}
              {!isCorrect && <> {fill(text.rightAnswer, { answer: `${LETTERS[question.correct_option]}. ${question.options[question.correct_option]}` })}</>}
            </p>
            {explanation && (
              <p><strong>{text.why}:</strong> {explanation}</p>
            )}
          </div>
        )}
      </div>

      {answered && (
        <button type="button" className="tk-btn tk-btn--primary tk-btn--block" onClick={onNext}>
          {isLast ? text.finish : text.next}
        </button>
      )}
    </div>
  );
}

/* Score summary + call to action to get the app. */
function Result({ text, score, total, onRestart }) {
  const passed = total > 0 && score / total >= PASS_RATIO;
  return (
    <div className="tk-quiz-center">
      <h2 className="tk-quiz-title">{text.resultTitle}</h2>
      <p className="tk-score">{fill(text.resultScore, { score, total })}</p>
      <p className="tk-quiz-muted">{passed ? text.resultPass : text.resultFail}</p>

      <div className="tk-quiz-cta" data-track-placement="quiz_result">
        <h3>{text.ctaTitle}</h3>
        <p>{text.ctaText}</p>
        <StoreLinks text={text} />
      </div>

      <button type="button" className="tk-btn tk-btn--ghost" onClick={onRestart}>{text.restart}</button>
    </div>
  );
}

export function StoreLinks({ text }) {
  return (
    <div className="tk-stores">
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
        <img src="/images/Download_on_the_App_Store_Badge.svg" alt={text.appStoreAlt} width="120" height="40" loading="lazy" className="tk-store-apple" />
      </a>
      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
        <img src="/images/google-play-badge.png" alt={text.googlePlayAlt} width="646" height="250" loading="lazy" className="tk-store-google" />
      </a>
    </div>
  );
}
